// src/components/product/ProductBasicInfoSection.tsx

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, Stack, TextField, Typography } from '@mui/material';

const softFieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'action.hover',
    borderRadius: 1,
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.06)',
    },
    '&:hover fieldset': {
      borderColor: 'divider',
    },
    '&.Mui-focused': {
      bgcolor: 'background.paper',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'primary.main',
    },
  },
} as const;

interface ProductBasicInfoSectionProps {
  /** Immutable product identifier shown to the user. */
  productCode: string;
  /** Hide product code when it is already shown in the page summary/header. */
  showProductCode?: boolean;
  /** Editable display name. */
  productName: string;
  /** Editable long description. */
  description: string;
  /** Sales price (USD). */
  price: number;
  /** Hide price when pricing is managed in a dedicated section. */
  showPrice?: boolean;

  /** Propagate product name changes to the parent form state. */
  onChangeName: (value: string) => void;
  /** Propagate description changes to the parent form state. */
  onChangeDescription: (value: string) => void;
  /** Propagate normalized numeric price to the parent form state. */
  onChangePrice: (value: number) => void;
}

/**
 * ProductBasicInfoSection
 *
 * Displays and edits the core product fields:
 * - Product code (read-only)
 * - Product name
 * - Description
 * - Price (USD)
 *
 * Price input is controlled as a string to allow natural typing (e.g. "12.", "0.1").
 * The parent form receives a normalized numeric value only on blur:
 * - Empty input is normalized to 0.00
 * - Values are formatted to 2 decimal places
 */
export const ProductPriceField = ({
  price,
  onChangePrice,
  fullWidth = false,
}: {
  price: number;
  onChangePrice: (value: number) => void;
  fullWidth?: boolean;
}) => {
  // Keep a string state for smooth typing; normalize to a number on blur only.
  const [priceInput, setPriceInput] = useState(() =>
    Number.isFinite(price) ? price.toFixed(2) : '',
  );

  // Sync local input when the parent value changes externally (e.g., initial load/reset).
  useEffect(() => {
    setPriceInput(Number.isFinite(price) ? price.toFixed(2) : '');
  }, [price]);

  const handlePriceChange = (raw: string) => {
    // Allow empty while editing (will be normalized on blur).
    if (raw === '') {
      setPriceInput('');
      return;
    }

    // Allow digits and an optional decimal part with up to 2 digits.
    // Examples: "12", "12.", "12.3", "12.34"
    if (!/^\d*\.?\d{0,2}$/.test(raw)) return;

    setPriceInput(raw);
  };

  const handlePriceBlur = () => {
    // Normalize empty input to 0.00.
    if (priceInput === '') {
      setPriceInput('0.00');
      onChangePrice(0);
      return;
    }

    const numeric = Number(priceInput);
    const normalized = Number.isNaN(numeric) ? 0 : numeric;
    const formatted = normalized.toFixed(2);

    setPriceInput(formatted);
    onChangePrice(Number(formatted));
  };

  return (
    <TextField
      label="Price"
      size="small"
      type="text"
      value={priceInput}
      onChange={(e) => handlePriceChange(e.target.value)}
      onBlur={handlePriceBlur}
      fullWidth={fullWidth}
      sx={fullWidth ? undefined : { maxWidth: 200 }}
      helperText="Sales price in USD (max 2 decimal places)."
    />
  );
};

const ProductBasicInfoSection = ({
  productCode,
  showProductCode = true,
  productName,
  description,
  price,
  showPrice = true,
  onChangeName,
  onChangeDescription,
  onChangePrice,
}: ProductBasicInfoSectionProps) => {
  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        mb: 3,
      })}
    >
      <CardHeader
        title={
          <Typography variant="h6" fontWeight={700}>
            General
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Edit the product name and supplier-provided description.
          </Typography>
        }
      />

      <CardContent>
        <Stack spacing={2.5}>
          {showProductCode ? (
            <TextField
              label="Product Code"
              value={productCode}
              InputProps={{ readOnly: true }}
              size="small"
              sx={{ maxWidth: 360 }}
            />
          ) : null}

          <TextField
            label="Product Name"
            placeholder="Enter product name"
            size="small"
            value={productName}
            onChange={(e) => onChangeName(e.target.value)}
            sx={{
              ...softFieldSx,
              '& .MuiInputBase-input': {
                fontWeight: 700,
              },
            }}
          />

          <TextField
            label="Description"
            placeholder="Describe the product"
            size="small"
            value={description || ''}
            onChange={(e) => onChangeDescription(e.target.value)}
            sx={softFieldSx}
            InputProps={{
              inputComponent: 'textarea' as any,
              inputProps: {
                style: {
                  resize: 'vertical',
                  minHeight: 220,
                  lineHeight: 1.55,
                  paddingTop: 6,
                  paddingBottom: 6,
                  overflow: 'auto',
                },
              },
            }}
          />

          {showPrice ? (
            <ProductPriceField price={price} onChangePrice={onChangePrice} />
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ProductBasicInfoSection;
